import { useMatchMedia } from '@lux/hooks';
import { screenSizes } from '@lux/core';
import { Campaign } from '@shared/types/mylounge';

export type ColumnCount = 1 | 2 | 3 | 4;

/**
 * Custom hook for responsive column count using native matchMedia
 *
 * Uses standard Lux breakpoints for consistent responsive behavior:
 * - Mobile: 1 column (< sm)
 * - Tablet: 2 columns (sm - md)
 * - Desktop: 3 columns (md - lg)
 * - Large Desktop: 4 columns (>= lg)
 *
 * @returns Current column count that updates responsively
 */
export const useResponsiveColumns = (): ColumnCount => {
    const isTablet = useMatchMedia(`(min-width: ${screenSizes.xs}px) and (max-width: 801px)`);
    const isDesktop = useMatchMedia(`(min-width: 802px) and (max-width: 1077px)`);
    const isLargeDesktop = useMatchMedia(`(min-width: 1078px)`);

    if (isLargeDesktop) {
        return 4;
    }
    if (isDesktop) {
        return 3;
    }
    if (isTablet) {
        return 2;
    }
    return 1;
};

/**
 * Creates a unified, ordered campaign list for declarative grid rendering.
 *
 * Assembles campaigns in order: [early access campaigns] + [exclusive at position] + [regular campaigns]
 * This allows rendering with a natural loop where each campaign carries its type via plusBenefit.
 *
 * **IMPORTANT CONTRACT:**
 * - Does NOT deduplicate: If same campaign appears in multiple arrays, all instances are included
 * - Mutates internal unified list (input arrays are not modified - uses spread operator)
 * - Invalid exclusive positions (negative or non-integer) prevent exclusive campaign insertion
 * - Out-of-bounds positions append exclusive campaign at array end
 *
 * @param campaigns - Regular campaigns array (early access campaigns excluded here)
 * @param earlyAccessCampaigns - Array of early access campaigns (can be empty)
 * @param exclusiveCampaign - The exclusive campaign to insert (optional, undefined means no insertion)
 * @param exclusivePosition - Zero-based position for exclusive campaign. Must be non-negative integer
 *
 * @returns New unified array: [early access...] + [exclusive] + [regular...]
 * @example
 * createCompleteCampaignList(
 *   [{id: 'r1'}, {id: 'r2'}],
 *   [{id: 'ea1'}],
 *   {id: 'ex1'},
 *   1
 * )
 * // Returns: [{id: 'ea1'}, {id: 'ex1'}, {id: 'r1'}, {id: 'r2'}]
 */
export const createCompleteCampaignList = (
    campaigns: Campaign[],
    earlyAccessCampaigns: Campaign[],
    exclusiveCampaign: Campaign | undefined,
    exclusivePosition: number,
): Campaign[] => {
    // Assemble: early access + normal campaigns
    const unifiedList = [...earlyAccessCampaigns, ...campaigns];

    // Insert exclusive campaign at calculated position if valid
    if (exclusiveCampaign && Number.isInteger(exclusivePosition) && exclusivePosition >= 0) {
        unifiedList.splice(exclusivePosition, 0, exclusiveCampaign);
    }

    return unifiedList;
};
